import { type MouseEvent, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActiveSuggestedAction,
  SUGGESTED_ACTION_TYPE,
} from 'router/subrouters/DataRoom/models/Dashboard';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import DataRoomUploadContext from 'router/subrouters/DataRoom/providers/DataRoomUpload/DataRoomUpload.context';
import { getDataRoomRoute, getSellingPortfolio } from 'utils';
import { OpenProlongModal } from './useProlongSecureLink';
import { DataRoomSubroute } from 'router/subrouters/DataRoom/models/DataRoom';
import { getLeadBoardSuggestionSearch } from '../utils';

const useGetSuggestedAction = (
  suggestion: ActiveSuggestedAction,
  openProlongModal?: OpenProlongModal,
  openFaqDrawer?: () => void,
  openQuestionDrawer?: () => void,
  actionSuggestedAction?: () => void,
) => {
  const { type, count, shareableLinkId, expiredAt } = suggestion;

  const {
    dataRoom: { _id, dealRoomId: dataRoomDealRoomId },
  } = useContext(DataRoomContext);
  const { onUploadClick } = useContext(DataRoomUploadContext);

  const navigate = useNavigate();

  const navigateToDataRoomPath = useCallback(
    (subroute: DataRoomSubroute, search?: string) => {
      actionSuggestedAction?.();
      navigate({
        pathname: getDataRoomRoute(_id, subroute),
        search,
      });
    },
    [_id, actionSuggestedAction, navigate],
  );

  const navigateToLeadBoard = useCallback(() => {
    const dealRoomId = suggestion.dealRoomId ?? dataRoomDealRoomId;

    if (!dealRoomId) return;

    actionSuggestedAction?.();
    navigate({
      pathname: getSellingPortfolio(dealRoomId, 'leads'),
      search: getLeadBoardSuggestionSearch(suggestion),
    });
  }, [suggestion, dataRoomDealRoomId, actionSuggestedAction, navigate]);

  const handleSingleItemOrNavigate = useCallback(
    (singleItemAction: () => void, subroute: DataRoomSubroute) => {
      if (count === 1) {
        singleItemAction();

        return;
      }

      navigateToDataRoomPath(subroute);
    },
    [count, navigateToDataRoomPath],
  );

  const uploadFiles = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      actionSuggestedAction?.();
      onUploadClick(e);
    },
    [actionSuggestedAction, onUploadClick],
  );

  const suggestedActionHandlers = useMemo(() => {
    return {
      [SUGGESTED_ACTION_TYPE.SECURE_LINKS_EXPIRING]: () =>
        handleSingleItemOrNavigate(
          () => openProlongModal?.(shareableLinkId, expiredAt),
          'shareable-links',
        ),
      [SUGGESTED_ACTION_TYPE.UNREPLIED_QUESTIONS]: () =>
        handleSingleItemOrNavigate(
          openQuestionDrawer,
          'questions/questions-and-answers',
        ),
      [SUGGESTED_ACTION_TYPE.NO_FAQS]: openFaqDrawer,
      [SUGGESTED_ACTION_TYPE.DATA_ROOM_EMPTY]: uploadFiles,
      [SUGGESTED_ACTION_TYPE.NO_FILES_LAST_10_DAYS]: uploadFiles,
      [SUGGESTED_ACTION_TYPE.PEOPLE_IN_WAITING_ROOM]: () =>
        navigateToDataRoomPath('waiting-room'),
      [SUGGESTED_ACTION_TYPE.NEW_FILE_REQUEST]: () =>
        navigateToDataRoomPath('rfi/list'),
      [SUGGESTED_ACTION_TYPE.EMPTY_SECURE_LINKS]: () =>
        navigateToDataRoomPath('shareable-links', '?sort=filesCount'),
      [SUGGESTED_ACTION_TYPE.UNREAD_MESSAGES]: navigateToLeadBoard,
      [SUGGESTED_ACTION_TYPE.UNREAD_LEAD_NOTES]: navigateToLeadBoard,
      [SUGGESTED_ACTION_TYPE.UNREAD_DEAL_NOTES]: navigateToLeadBoard,
    };
  }, [
    shareableLinkId,
    expiredAt,
    uploadFiles,
    handleSingleItemOrNavigate,
    openProlongModal,
    openQuestionDrawer,
    openFaqDrawer,
    navigateToDataRoomPath,
    navigateToLeadBoard,
  ]);

  return suggestedActionHandlers[type];
};

export default useGetSuggestedAction;
